import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ALL_CHAINS, getExplorerTxUrl } from "../../../../anyspend/index.js";
import { ChainTokenIcon } from "../../../../anyspend/react/components/common/ChainTokenIcon.js";
import { toast, useAccountWallet, useAnalytics, useModalStore, useProfile, useSimBalance, useUnifiedChainSwitchAndExecute } from "../../../../global-account/react/index.js";
import { validateImageUrl } from "../../../../global-account/react/utils/profileDisplay.js";
import { formatDisplayNumber, formatTokenAmount } from "../../../../shared/utils/number.js";
import invariant from "invariant";
import { CircleHelp, Clock, Loader2, Send as SendIcon, Wallet } from "lucide-react";
import { useState } from "react";
import { NumericFormat } from "react-number-format";
import { encodeFunctionData, erc20Abi, isAddress, parseUnits } from "viem";
import { useRecentAddressesStore } from "../../stores/useRecentAddressesStore.js";
import ModalHeader from "../ModalHeader/ModalHeader.js";
import { Button } from "../ui/button.js";
// Component for displaying a recent address with profile data
function RecentAddressItem(t0) {
  const $ = _c(7);
  const {
    address,
    onClick
  } = t0;
  let t1;
  if ($[0] !== address) {
    t1 = {
      address
    };
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: profileData
  } = useProfile(t1);
  const t2 = profileData?.avatar;
  let t3;
  if ($[2] !== address || $[3] !== onClick || $[4] !== profileData || $[5] !== t2) {
    const validatedAvatar = validateImageUrl(t2);
    t3 = _jsxs("button", {
      onClick,
      className: "flex items-center gap-2 rounded-xl px-3 py-2 transition-colors hover:bg-[#fafafa]",
      children: [validatedAvatar ? _jsx("img", {
        src: validatedAvatar,
        alt: profileData?.name || address,
        className: "h-10 w-10 rounded-full"
      }) : _jsx("div", {
        className: "flex h-10 w-10 items-center justify-center rounded-full border border-[#e4e4e7] bg-[#f4f4f5]",
        children: _jsx(Wallet, {
          className: "h-5 w-5 text-[#a0a0ab]"
        })
      }), _jsx("div", {
        className: "flex flex-col items-start",
        children: _jsxs("span", {
          className: "font-neue-montreal-medium text-base tracking-[-0.32px] text-[#70707b]",
          children: [address.slice(0, 6), "...", address.slice(-4), profileData?.name && ` (${profileData.name})`]
        })
      })]
    });
    $[2] = address;
    $[3] = onClick;
    $[4] = profileData;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function Send({
  recipientAddress: initialRecipient,
  onSuccess
}) {
  const {
    address
  } = useAccountWallet();
  const navigateBack = useModalStore(state => state.navigateBack);
  const setB3ModalOpen = useModalStore(state_0 => state_0.setB3ModalOpen);
  // Wizard state
  const [step, setStep] = useState("recipient");
  const [selectedToken, setSelectedToken] = useState(null);
  const [recipientAddress, setRecipientAddress] = useState(initialRecipient || "");
  const [sendAmount, setSendAmount] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [showValidatedResult, setShowValidatedResult] = useState(false);
  // Hooks
  const {
    data: simBalance,
    refetch: refetchSimBalance,
    isLoading: isLoadingBalance
  } = useSimBalance(address);
  const {
    switchChainAndExecute
  } = useUnifiedChainSwitchAndExecute();
  const {
    sendAnalyticsEvent
  } = useAnalytics();
  // Recent addresses store
  const recentAddresses = useRecentAddressesStore(state_1 => state_1.recentAddresses);
  const addRecentAddress = useRecentAddressesStore(state_2 => state_2.addRecentAddress);
  // Fetch profile data for validated address
  const {
    data: validatedProfileData
  } = useProfile({
    address: showValidatedResult && recipientAddress && isAddress(recipientAddress) ? recipientAddress : undefined
  });
  const validatedRecipientAvatar = validateImageUrl(validatedProfileData?.avatar);
  // Address validation
  const handleRecipientAddressChange = value => {
    setRecipientAddress(value);
    setShowValidatedResult(false);
    if (value && !isAddress(value)) {
      setAddressError("Please enter a valid EVM address (0x...)");
    } else {
      setAddressError("");
      if (value && isAddress(value)) {
        setShowValidatedResult(true);
      }
    }
  };
  // Paste from clipboard
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const trimmedText = text.trim();
      setRecipientAddress(trimmedText);
      if (trimmedText && isAddress(trimmedText)) {
        setAddressError("");
        setShowValidatedResult(true);
      } else if (trimmedText) {
        setAddressError("Please enter a valid EVM address (0x...)");
        setShowValidatedResult(false);
      }
    } catch (error) {
      toast.error("Failed to read clipboard");
    }
  };
  // Handle clicking on the validated result to proceed
  const handleSelectValidatedAddress = () => {
    if (recipientAddress && isAddress(recipientAddress)) {
      addRecentAddress(recipientAddress);
      setStep("token");
    }
  };
  // Go to previous step
  const handleBack = () => {
    if (step === "token") setStep("recipient");else if (step === "amount") setStep("token");else if (step === "confirm") setStep("amount");else navigateBack();
  };
  // Get current selected token from fresh balance
  const getCurrentSelectedToken = () => {
    if (!selectedToken || !simBalance?.balances) {
      return null;
    }
    const found = simBalance.balances.find(token => token.chain_id === selectedToken.chain_id && token.address === selectedToken.address);
    return found || null;
  };
  // Percentage button handler
  const handlePercentageClick = percentage => {
    const currentToken = getCurrentSelectedToken();
    if (currentToken) {
      const tokenBalance = BigInt(currentToken.amount) * BigInt(percentage) / BigInt(100);
      const amount = formatTokenAmount(tokenBalance, currentToken.decimals, 30, false);
      setSendAmount(amount);
    }
  };
  // Send transaction
  const handleSend = async () => {
    const sendAmountWithoutCommas = sendAmount.replace(/,/g, "");
    const currentToken_0 = getCurrentSelectedToken();
    if (!currentToken_0 || !recipientAddress || !sendAmount || parseFloat(sendAmountWithoutCommas) <= 0) {
      return;
    }
    setIsSending(true);
    const amountInWei = parseUnits(sendAmountWithoutCommas, currentToken_0.decimals);
    const analyticsData = {
      amount: sendAmount,
      symbol: currentToken_0.symbol,
      chain_id: currentToken_0.chain_id,
      address: currentToken_0.address
    };
    try {
      invariant(isAddress(recipientAddress), "Recipient address is not a valid address");
      const sendTokenData = encodeFunctionData({
        abi: erc20Abi,
        functionName: "transfer",
        args: [recipientAddress, amountInWei]
      });
      const tx = await switchChainAndExecute(currentToken_0.chain_id, {
        to: currentToken_0.address === "native" ? recipientAddress : currentToken_0.address,
        data: sendTokenData,
        value: currentToken_0.address === "native" ? amountInWei : BigInt(0)
      });
      if (tx) {
        sendAnalyticsEvent("send_token_button_click", {
          ...analyticsData,
          success: true,
          tx: getExplorerTxUrl(currentToken_0.chain_id, tx)
        });
        setStep("success");
        toast.success(`Successfully sent ${sendAmount} ${currentToken_0.symbol}`);
        if (onSuccess) {
          onSuccess(tx);
        }
        setTimeout(async () => {
          await refetchSimBalance();
        }, 1000);
      }
    } catch (error_0) {
      sendAnalyticsEvent("send_token_button_click", {
        ...analyticsData,
        success: false,
        reason: error_0.message || "Unknown error"
      });
      toast.error(`Failed to send ${currentToken_0.symbol}: ${error_0.message || "Unknown error"}`);
    } finally {
      setIsSending(false);
    }
  };
  // Get step title
  const getStepTitle = () => {
    switch (step) {
      case "recipient":
        return "Select Recipient";
      case "token":
        return "Select Token";
      case "amount":
        return "Enter Amount";
      case "confirm":
        return "Confirm";
      case "success":
        return "Sent!";
      default:
        return "Send";
    }
  };
  return _jsxs("div", {
    className: "dark:bg-b3-background flex h-[600px] w-full flex-col bg-white",
    children: [_jsx(ModalHeader, {
      handleBack: handleBack,
      title: getStepTitle()
    }), _jsxs("div", {
      className: "flex-1 overflow-y-auto",
      children: [step === "recipient" && _jsxs("div", {
        className: "flex flex-col gap-6 p-5",
        children: [_jsxs("div", {
          className: "dark:border-b3-line dark:bg-b3-background flex h-12 w-full items-stretch overflow-hidden rounded-lg border border-[#d1d1d6] bg-white",
          children: [_jsx("div", {
            className: "flex w-12 items-center justify-center bg-transparent px-3 py-2",
            children: _jsx("span", {
              className: "font-neue-montreal-medium text-base text-[#3f3f46] dark:text-white",
              children: "To"
            })
          }), _jsxs("div", {
            className: "flex flex-1 items-center border-l border-[#d1d1d6] px-3 py-2",
            children: [_jsx("input", {
              type: "text",
              value: recipientAddress,
              onChange: e => handleRecipientAddressChange(e.target.value),
              placeholder: "ENS or Address",
              className: "font-neue-montreal-medium dark:bg-b3-background flex-1 text-base text-[#70707b] outline-none placeholder:text-[#70707b] dark:text-white dark:placeholder:text-white"
            }), _jsx("button", {
              onClick: handlePaste,
              className: "font-inter ml-2 rounded-md border border-[#e4e4e7] bg-[#fafafa] px-2.5 py-0.5 text-sm font-medium text-[#3f3f46] transition-colors hover:bg-[#f4f4f5]",
              children: "Paste"
            })]
          })]
        }), addressError && _jsx("p", {
          className: "font-neue-montreal-medium -mt-4 text-xs text-red-500",
          children: addressError
        }), showValidatedResult && recipientAddress && isAddress(recipientAddress) && _jsxs("div", {
          className: "flex flex-col gap-2",
          children: [_jsx("div", {
            className: "flex items-center gap-1",
            children: _jsx("span", {
              className: "font-sf-pro-text text-sm font-semibold leading-[1.3] tracking-[-0.41px] text-[#0b57c2]",
              children: "Result"
            })
          }), _jsxs("button", {
            onClick: handleSelectValidatedAddress,
            className: "dark:bg-b3-background dark:border-b3-line flex items-center gap-2 rounded-xl bg-[#f4f4f5] px-3 py-2 transition-colors hover:bg-[#e4e4e7]",
            children: [validatedRecipientAvatar ? _jsx("img", {
              src: validatedRecipientAvatar,
              alt: validatedProfileData?.name || recipientAddress,
              className: "h-10 w-10 rounded-full"
            }) : _jsx("div", {
              className: "dark:border-b3-line dark:bg-b3-background flex h-10 w-10 items-center justify-center rounded-full border border-[#e4e4e7] bg-[#f4f4f5]",
              children: _jsx(Wallet, {
                className: "h-5 w-5 text-[#a0a0ab] dark:text-white"
              })
            }), _jsxs("span", {
              className: "font-neue-montreal-medium text-base tracking-[-0.32px] text-[#70707b] dark:text-white",
              children: [recipientAddress.slice(0, 6), "...", recipientAddress.slice(-4), validatedProfileData?.name && ` (${validatedProfileData.name})`]
            })]
          })]
        }), recentAddresses.length > 0 && _jsxs("div", {
          className: "flex flex-col gap-2",
          children: [_jsxs("div", {
            className: "flex items-center gap-1",
            children: [_jsx(Clock, {
              className: "h-3.5 w-3.5 text-[#3f3f46]"
            }), _jsx("span", {
              className: "font-sf-pro-text text-sm font-semibold leading-[1.3] tracking-[-0.41px] text-[#3f3f46]",
              children: "Recents"
            })]
          }), _jsx("div", {
            className: "flex flex-col",
            children: recentAddresses.map((recent, index) => _jsx(RecentAddressItem, {
              address: recent.address,
              onClick: () => {
                // Just fill the input and show validation - don't auto-proceed
                handleRecipientAddressChange(recent.address);
              }
            }, index))
          })]
        })]
      }), step === "token" && _jsx("div", {
        className: "flex flex-col p-5",
        children: isLoadingBalance ? _jsx("div", {
          className: "space-y-4",
          children: _jsx("div", {
            className: "space-y-1",
            children: [...Array(3)].map((_, index_0) => _jsxs("div", {
              className: "flex items-center justify-between rounded-xl p-3",
              children: [_jsxs("div", {
                className: "flex items-center gap-3",
                children: [_jsx("div", {
                  className: "bg-b3-line h-10 w-10 animate-pulse rounded-full"
                }), _jsxs("div", {
                  children: [_jsx("div", {
                    className: "bg-b3-line mb-1 h-4 w-16 animate-pulse rounded"
                  }), _jsx("div", {
                    className: "bg-b3-line h-3 w-24 animate-pulse rounded"
                  })]
                })]
              }), _jsxs("div", {
                className: "text-right",
                children: [_jsx("div", {
                  className: "bg-b3-line mb-1 h-4 w-20 animate-pulse rounded"
                }), _jsx("div", {
                  className: "bg-b3-line h-3 w-16 animate-pulse rounded"
                })]
              })]
            }, index_0))
          })
        }) : simBalance?.balances && simBalance.balances.length > 0 ? _jsx("div", {
          className: "space-y-4",
          children: _jsx("div", {
            className: "space-y-1",
            children: simBalance.balances.map(token_0 => _jsxs("div", {
              className: "hover:bg-b3-line/60 dark:hover:bg-b3-primary-wash/40 group flex cursor-pointer items-center justify-between rounded-xl p-3 transition-all duration-200",
              onClick: () => {
                setSelectedToken(token_0);
                setStep("amount");
              },
              children: [_jsxs("div", {
                className: "flex items-center gap-3",
                children: [_jsx("div", {
                  className: "flex h-10 w-10 items-center justify-center rounded-full",
                  children: ALL_CHAINS[token_0.chain_id]?.logoUrl ? _jsx(ChainTokenIcon, {
                    chainUrl: ALL_CHAINS[token_0.chain_id].logoUrl,
                    tokenUrl: token_0.token_metadata?.logo,
                    className: "size-10"
                  }) : _jsx(CircleHelp, {
                    className: "text-b3-react-foreground size-10"
                  })
                }), _jsxs("div", {
                  children: [_jsx("div", {
                    className: "flex items-center gap-2",
                    children: _jsx("span", {
                      className: "text-b3-grey font-neue-montreal-semibold transition-colors duration-200 group-hover:font-bold group-hover:text-black",
                      children: token_0.symbol
                    })
                  }), _jsx("div", {
                    className: "text-b3-foreground-muted font-neue-montreal-medium text-sm transition-colors duration-200 group-hover:text-gray-700",
                    children: token_0.name
                  })]
                })]
              }), _jsxs("div", {
                className: "text-right",
                children: [_jsx("div", {
                  className: "text-b3-grey font-neue-montreal-semibold transition-colors duration-200 group-hover:font-bold group-hover:text-black",
                  children: formatTokenAmount(BigInt(token_0.amount), token_0.decimals)
                }), _jsx("div", {
                  className: "text-b3-foreground-muted font-neue-montreal-medium text-sm transition-colors duration-200 group-hover:text-gray-700",
                  children: formatDisplayNumber(token_0.value_usd, {
                    style: "currency",
                    fractionDigits: 2
                  })
                })]
              })]
            }, token_0.chain_id + "_" + token_0.address))
          })
        }) : _jsxs("div", {
          className: "flex flex-col items-center justify-center py-12 text-center",
          children: [_jsx(CircleHelp, {
            className: "text-b3-foreground-muted mb-4 h-8 w-8"
          }), _jsx("p", {
            className: "text-b3-foreground-muted font-neue-montreal-medium text-sm",
            children: "No tokens available"
          })]
        })
      }), step === "amount" && selectedToken && _jsxs("div", {
        className: "flex flex-col gap-6 p-5",
        children: [_jsxs("div", {
          className: "dark:border-b3-line dark:bg-b3-background flex items-center justify-between rounded-xl border border-[#d1d1d6] bg-[#fafafa] p-3",
          children: [_jsxs("div", {
            className: "flex items-center gap-3",
            children: [_jsx("div", {
              className: "flex h-10 w-10 items-center justify-center",
              children: ALL_CHAINS[selectedToken.chain_id]?.logoUrl ? _jsx(ChainTokenIcon, {
                chainUrl: ALL_CHAINS[selectedToken.chain_id].logoUrl,
                tokenUrl: selectedToken.token_metadata?.logo,
                className: "size-10"
              }) : _jsx(CircleHelp, {
                className: "text-b3-react-foreground size-10"
              })
            }), _jsxs("div", {
              children: [_jsx("div", {
                className: "font-neue-montreal-semibold text-base text-[#18181b] dark:text-white",
                children: selectedToken.symbol
              }), _jsx("div", {
                className: "font-neue-montreal-medium text-sm text-[#70707b] dark:text-white",
                children: formatTokenAmount(BigInt(selectedToken.amount), selectedToken.decimals)
              })]
            })]
          }), _jsx("button", {
            onClick: () => setStep("token"),
            className: "text-b3-primary-blue font-neue-montreal-semibold hover:text-b3-primary-blue/80 text-sm transition-colors dark:text-white",
            children: "Change"
          })]
        }), _jsxs("div", {
          className: "space-y-3",
          children: [_jsx(NumericFormat, {
            decimalSeparator: ".",
            allowedDecimalSeparators: [","],
            thousandSeparator: true,
            inputMode: "decimal",
            autoComplete: "off",
            autoCorrect: "off",
            type: "text",
            placeholder: "0.00",
            minLength: 1,
            maxLength: 30,
            spellCheck: "false",
            className: "font-neue-montreal-medium placeholder:text-b3-foreground-muted dark:border-b3-line dark:bg-b3-background w-full rounded-lg border border-[#d1d1d6] bg-white px-3 py-2 text-base text-[#18181b] outline-none focus:border-[#0c68e9] dark:text-white",
            pattern: "^[0-9]*[.,]?[0-9]*$",
            disabled: isSending,
            value: sendAmount,
            allowNegative: false,
            onValueChange: values => setSendAmount(values.value)
          }), _jsx("div", {
            className: "grid grid-cols-4 gap-2",
            children: [25, 50, 75, 100].map(percentage_0 => _jsxs(Button, {
              variant: "outline",
              onClick: () => handlePercentageClick(percentage_0),
              className: "font-neue-montreal-medium dark:border-b3-line dark:bg-b3-background border-[#d1d1d6] text-sm text-[#18181b] hover:bg-[#fafafa] dark:text-white",
              disabled: isSending,
              children: [percentage_0, "%"]
            }, percentage_0))
          }), _jsxs("div", {
            className: "font-neue-montreal-medium text-sm text-[#70707b]",
            children: ["Available: ", formatTokenAmount(BigInt(selectedToken.amount), selectedToken.decimals), " ", selectedToken.symbol]
          })]
        }), _jsx(Button, {
          onClick: () => setStep("confirm"),
          disabled: !sendAmount || parseFloat(sendAmount) <= 0,
          className: "bg-b3-primary-blue hover:bg-b3-primary-blue/90 font-neue-montreal-semibold disabled:bg-b3-line disabled:text-b3-foreground-muted h-12 w-full rounded-xl text-white",
          children: "Continue"
        })]
      }), step === "confirm" && selectedToken && _jsxs("div", {
        className: "flex min-h-full flex-col",
        children: [_jsxs("div", {
          className: "flex flex-col items-center gap-4 px-5 pb-0 pt-6",
          children: [_jsx("div", {
            className: "dark:bg-b3-line flex h-14 w-14 items-center justify-center rounded-full bg-[#d5e5fd]",
            children: _jsx(SendIcon, {
              className: "h-7 w-7 text-[#0c68e9]"
            })
          }), _jsxs("div", {
            className: "flex items-center gap-1",
            children: [_jsx("span", {
              className: "font-neue-montreal-semibold text-[30px] leading-[38px] text-[#18181b] dark:text-white",
              children: sendAmount
            }), _jsx("span", {
              className: "font-neue-montreal-semibold text-[30px] leading-[38px] text-[#70707b] dark:text-white",
              children: selectedToken.symbol
            })]
          })]
        }), _jsx("div", {
          className: "h-5"
        }), _jsx("div", {
          className: "flex flex-col gap-3 px-5",
          children: _jsx("div", {
            className: "dark:border-b3-line dark:bg-b3-background rounded-xl border border-[#e4e4e7] bg-[#fafafa] p-4",
            children: _jsxs("div", {
              className: "flex flex-col gap-3",
              children: [_jsxs("div", {
                className: "dark:border-b3-line flex items-center justify-between border-b border-[#e4e4e7] pb-3",
                children: [_jsx("span", {
                  className: "font-inter text-sm font-normal leading-5 text-[#51525c] dark:text-white",
                  children: "To"
                }), _jsxs("span", {
                  className: "font-inter text-sm font-normal leading-5 text-[#18181b] dark:text-white",
                  children: ["Wallet (", recipientAddress.slice(0, 6), "...", recipientAddress.slice(-4), ")"]
                })]
              }), _jsxs("div", {
                className: "dark:border-b3-line flex items-center justify-between border-b border-[#e4e4e7] pb-3",
                children: [_jsx("span", {
                  className: "font-inter text-sm font-normal leading-5 text-[#51525c] dark:text-white",
                  children: "Network"
                }), _jsxs("div", {
                  className: "flex items-center gap-1.5",
                  children: [_jsx("span", {
                    className: "font-inter text-sm font-normal leading-5 text-[#51525c] dark:text-white",
                    children: ALL_CHAINS[selectedToken.chain_id]?.name || "Unknown"
                  }), ALL_CHAINS[selectedToken.chain_id]?.logoUrl && _jsx("img", {
                    src: ALL_CHAINS[selectedToken.chain_id].logoUrl,
                    alt: ALL_CHAINS[selectedToken.chain_id]?.name,
                    className: "h-4 w-4 rounded-full"
                  })]
                })]
              }), _jsxs("div", {
                className: "flex items-center justify-between",
                children: [_jsx("span", {
                  className: "font-inter text-sm font-normal leading-5 text-[#51525c] dark:text-white",
                  children: "Network fee"
                }), _jsx("span", {
                  className: "font-inter text-sm font-normal leading-5 text-[#18181b] dark:text-white",
                  children: "$0.1"
                })]
              })]
            })
          })
        }), _jsx("div", {
          className: "flex-1"
        }), _jsxs("div", {
          className: "dark:border-b3-line dark:bg-b3-background flex gap-4 border-t border-[#e4e4e7] bg-[#fafafa] p-4",
          children: [_jsx(Button, {
            onClick: handleBack,
            disabled: isSending,
            variant: "outline",
            className: "font-inter h-12 flex-1 rounded-xl border border-[#e4e4e7] bg-white text-base font-semibold text-[#3f3f46] shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] hover:bg-[#fafafa]",
            children: "Cancel"
          }), _jsx(Button, {
            onClick: handleSend,
            disabled: isSending,
            className: "font-inter border-white/12 h-12 flex-1 rounded-xl border-2 bg-[#0c68e9] text-base font-semibold text-white shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] hover:bg-[#0b5fd4]",
            children: isSending ? _jsxs(_Fragment, {
              children: [_jsx(Loader2, {
                className: "mr-2 h-4 w-4 animate-spin"
              }), "Sending..."]
            }) : "Send"
          })]
        })]
      }), step === "success" && selectedToken && _jsxs("div", {
        className: "flex h-full flex-col items-center justify-center gap-6 p-5 text-center",
        children: [_jsx("div", {
          className: "flex h-14 w-14 items-center justify-center rounded-full bg-green-100",
          children: _jsx(SendIcon, {
            className: "h-7 w-7 text-green-600"
          })
        }), _jsxs("div", {
          children: [_jsx("h3", {
            className: "font-neue-montreal-semibold mb-2 text-xl text-[#18181b]",
            children: "Sent!"
          }), _jsxs("p", {
            className: "font-neue-montreal-medium text-sm text-[#70707b]",
            children: [sendAmount, " ", selectedToken.symbol, " was sent to ", recipientAddress.slice(0, 6), "...", recipientAddress.slice(-4)]
          })]
        }), _jsx(Button, {
          onClick: () => setB3ModalOpen(false),
          className: "bg-b3-primary-blue hover:bg-b3-primary-blue/90 font-neue-montreal-semibold h-12 w-full rounded-xl text-white",
          children: "Done"
        })]
      })]
    })]
  });
}